import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
} from "@mui/material";
import React from "react";
import transformDateToDe from "../../../functions/transformDateToDe";
import { AppointmentOverviewContext } from "../context/appointmentOverviewContext";
import Appointment from "../interface/appointment";
import ParticipantSearchProps from "../interface/participantSearchProps";
import RenderParticipantSearch from "./renderParticipantSearch";

const BookAppointmentDialog = (): React.JSX.Element | null => {
  const {
    bookingAppointment,
    setBookingAppointment,
    doBookAppointment,
    bookingLoading,
  }: {
    bookingAppointment: Appointment | null;
    setBookingAppointment: React.Dispatch<
      React.SetStateAction<Appointment | null>
    >;
    doBookAppointment: (participantId: number) => void;
    bookingLoading: boolean;
  } = React.useContext(AppointmentOverviewContext);

  const [participantSearch, setParticipantSearch] =
    React.useState<ParticipantSearchProps>({
      loading: false,
      searchString: "",
      searchResults: [],
      selectedParticipant: null,
    });

  if (!bookingAppointment) return null;

  // a booked appointment keeps its slot and moves to the selected participant
  const isRebooking: boolean = bookingAppointment.status === "G";

  const selectedParticipantIsAlreadyBooked: boolean =
    !!participantSearch.selectedParticipant &&
    participantSearch.selectedParticipant.participantId ===
      bookingAppointment.participantId;

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={() => setBookingAppointment(null)}
      aria-labelledby="book-appointment"
    >
      <DialogTitle id="book-appointment">
        {isRebooking ? "Termin umbuchen" : "Termin buchen"}
      </DialogTitle>

      <Divider />

      {/* the option list renders inside the dialog, so it needs room to open without scrolling */}
      <DialogContent sx={{ minHeight: 360 }}>
        <DialogContentText sx={{ mb: 1 }}>
          {`${bookingAppointment.weekDay}, ${transformDateToDe(
            bookingAppointment.date,
          )}, ${bookingAppointment.timeSlot} bei ${
            bookingAppointment.coachFullName
          }`}
        </DialogContentText>

        <DialogContentText sx={{ mb: 3 }}>
          {isRebooking
            ? `Aktuell gebucht: ${bookingAppointment.participantFullName}. Die bestehende Buchung wird storniert, die bisher gebuchte Person erhält eine Stornomail.`
            : "Der Termin wird direkt auf die ausgewählte Person gebucht. Teilnehmer:in und Coach erhalten eine Terminbestätigung."}
        </DialogContentText>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RenderParticipantSearch
              participantSearch={participantSearch}
              setParticipantSearch={setParticipantSearch}
            />
          </Grid>

          {selectedParticipantIsAlreadyBooked && (
            <Grid item xs={12}>
              <DialogContentText color="error">
                Diese:r Teilnehmer:in ist bereits auf diesen Termin gebucht.
              </DialogContentText>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: 2,
        }}
      >
        <Button onClick={() => setBookingAppointment(null)} color="primary">
          Abbrechen
        </Button>

        <Button
          disabled={
            !participantSearch.selectedParticipant ||
            selectedParticipantIsAlreadyBooked ||
            bookingLoading
          }
          onClick={() => {
            if (!participantSearch.selectedParticipant) return;

            doBookAppointment(
              participantSearch.selectedParticipant.participantId,
            );
          }}
          variant="contained"
          color="primary"
        >
          {isRebooking ? "Umbuchen" : "Buchen"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookAppointmentDialog;
